import React from 'react';

import { InputAdornment, MenuItem, Stack, TextField } from '@mui/material';

import { TourOfferPricingLevel } from '~/types/tours/tourOffer';

import {
  MAX_DISCOUNT,
  MAX_LUX_PLUS_DISCOUNT,
  MAX_MARKUP,
  fromPercent,
  levelProblems,
  toPercent,
} from './nativeTourPricing';

interface Props {
  value: TourOfferPricingLevel;
  onChange: (value: TourOfferPricingLevel) => void;
  /** The adjustment option for following the level above, e.g. "Follow the tour". */
  followText: string;
  size?: 'small' | 'medium';
  /** Off when the room has a LUX+ price of its own. */
  isLuxPlusDisabled?: boolean;
}

type Adjustment = 'follow' | 'discount' | 'markup';

/** One level's discount or markup and LUX+ discount. A blank field follows the level above. */
export default function NativeTourPricingLevelField({
  value,
  onChange,
  followText,
  size = 'small',
  isLuxPlusDisabled = false,
}: Props) {
  const adjustment: Adjustment = value.discount !== null ? 'discount' : value.markup !== null ? 'markup' : 'follow';
  const percent = adjustment === 'discount' ? value.discount : adjustment === 'markup' ? value.markup : null;
  const max = adjustment === 'markup' ? MAX_MARKUP : MAX_DISCOUNT;
  const problems = levelProblems(value);
  const adjustmentProblem = problems.find((problem) => !problem.startsWith('LUX+'));
  const luxPlusProblem = problems.find((problem) => problem.startsWith('LUX+'));

  const setAdjustment = (next: Adjustment, nextPercent: number | null) =>
    onChange({
      ...value,
      discount: next === 'discount' ? (nextPercent ?? 0) : null,
      markup: next === 'markup' ? (nextPercent ?? 0) : null,
    });

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
      <TextField
        select
        size={size}
        label="Adjustment"
        value={adjustment}
        onChange={(event) => setAdjustment(event.target.value as Adjustment, percent)}
        sx={{ width: { sm: 200 } }}
      >
        <MenuItem value="follow">{followText}</MenuItem>
        <MenuItem value="discount">Discount</MenuItem>
        <MenuItem value="markup">Markup</MenuItem>
      </TextField>
      {adjustment !== 'follow' && (
        <TextField
          size={size}
          type="number"
          label={adjustment === 'markup' ? 'Markup' : 'Discount'}
          value={toPercent(percent)}
          onChange={(event) => setAdjustment(adjustment, fromPercent(event.target.value))}
          error={!!adjustmentProblem}
          helperText={adjustmentProblem}
          inputProps={{ min: 0, max: max * 100, step: 1 }}
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          sx={{ width: { sm: 140 } }}
        />
      )}
      <TextField
        size={size}
        type="number"
        label="LUX+ discount"
        value={toPercent(value.luxPlusDiscount)}
        disabled={isLuxPlusDisabled}
        onChange={(event) => onChange({ ...value, luxPlusDiscount: fromPercent(event.target.value) })}
        error={!!luxPlusProblem}
        helperText={luxPlusProblem}
        inputProps={{ min: 0, max: MAX_LUX_PLUS_DISCOUNT * 100, step: 1 }}
        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
        sx={{ width: { sm: 160 } }}
      />
    </Stack>
  );
}
